import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  Grid,
  Button,
  Stack,
  IconButton,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  alpha,
  useTheme,
  Paper,
  Slide,
  Divider,
  Avatar,
  useMediaQuery
} from "@mui/material";
import {
  Search as SearchIcon,
  Delete as TrashIcon,
  Visibility as ViewIcon,
  FileDownload as DownloadIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  ConfirmationNumber as TicketIcon,
  AccountBalanceWallet as RevenueIcon,
  EventAvailable as ConfirmedIcon,
  History as PendingIcon,
  Cancel as CancelIcon,
  CheckCircle as SuccessIcon,
  VerifiedUser as TrustIcon
} from "@mui/icons-material";
import { motion } from "framer-motion";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { toast } from "react-toastify";
import api from "../../services/api";
import ModernLoader from "../../components/common/ModernLoader";
import moment from "moment";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const AdminBookings = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [openDetails, setOpenDetails] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);

  const fetchBookings = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      else setRefreshing(true);

      const timestamp = new Date().getTime();
      const response = await api.get(`/bookings?limit=500&_t=${timestamp}`);
      let data = [];
      if (Array.isArray(response.data)) data = response.data;
      else if (response.data.bookings) data = response.data.bookings;
      else if (response.data.data) data = response.data.data;

      setBookings(data);
    } catch (error) {
      console.error("Fetch bookings error:", error);
      toast.error("Failed to synchronize booking ledger");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchBookings();

    // Real-time synchronization for the booking ledger
    const handleSync = () => {
      console.log("🔄 Booking ledger sync triggered by real-time event...");
      fetchBookings(false);
    };

    window.addEventListener("seats-booked", handleSync);
    window.addEventListener("booking-updated", handleSync);
    window.addEventListener("booking-deleted", handleSync);

    return () => {
      window.removeEventListener("seats-booked", handleSync);
      window.removeEventListener("booking-updated", handleSync);
      window.removeEventListener("booking-deleted", handleSync);
    };
  }, [fetchBookings]);

  const handleStatusUpdate = async (id, newStatus) => {
    try {
      await api.put(`/bookings/${id}`, { status: newStatus });
      toast.success(`Booking status transitioned to ${newStatus.toUpperCase()}`);
      fetchBookings(false);
      if (selectedBooking?._id === id) setOpenDetails(false);
    } catch (error) {
      toast.error("Status transition failed");
    }
  };

  const handleDelete = async (id, bookingId) => {
    if (window.confirm(`Permanently expunge booking [${bookingId}]? This cannot be undone.`)) {
      try {
        await api.delete(`/bookings/${id}`);
        toast.success("Ledger entry expunged");
        fetchBookings(false);
      } catch (error) {
        toast.error("Process failed");
      }
    }
  };

  const columns = useMemo(() => [
    {
      accessorKey: "bookingId",
      header: "Reference ID",
      Header: ({ column }) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <TicketIcon sx={{ fontSize: 18, color: "primary.main" }} />
          <Typography variant="caption" sx={{ fontWeight: 900, textTransform: "uppercase", letterSpacing: "1px" }}>{column.columnDef.header}</Typography>
        </Stack>
      ),
      Cell: ({ cell }) => (
        <Typography variant="body2" fontWeight={900} sx={{ letterSpacing: "0.05em", color: "primary.main", bgcolor: alpha(theme.palette.primary.main, 0.05), px: 1, py: 0.5, borderRadius: "6px", width: "fit-content" }}>
          {cell.getValue()?.toUpperCase() || "N/A"}
        </Typography>
      ),
    },
    {
      id: "customer",
      header: "Registry Holder",
      accessorFn: (row) => `${row.customerName || row.user?.name || 'Guest'}`,
      Header: ({ column }) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <FilterIcon sx={{ fontSize: 18, color: "secondary.main" }} />
          <Typography variant="caption" sx={{ fontWeight: 900, textTransform: "uppercase", letterSpacing: "1px" }}>{column.columnDef.header}</Typography>
        </Stack>
      ),
      Cell: ({ row }) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Avatar sx={{ width: 34, height: 34, fontSize: "0.85rem", fontWeight: 900, bgcolor: alpha(theme.palette.secondary.main, 0.1), color: "secondary.main" }}>
            {(row.original.customerName || row.original.user?.name || 'G')[0]}
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight={850} sx={{ color: "text.primary" }}>{row.original.customerName || row.original.user?.name || 'Guest User'}</Typography>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>{row.original.customerEmail || row.original.user?.email || 'N/A'}</Typography>
          </Box>
        </Box>
      ),
    },
    {
      accessorKey: "movieTitle",
      header: "Asset",
      accessorFn: (row) => row.show?.movie?.title || row.movieTitle || "Legacy Asset",
      Header: ({ column }) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <ConfirmedIcon sx={{ fontSize: 18, color: "info.main" }} />
          <Typography variant="caption" sx={{ fontWeight: 900, textTransform: "uppercase", letterSpacing: "1px" }}>{column.columnDef.header}</Typography>
        </Stack>
      ),
      Cell: ({ cell }) => (
        <Typography variant="body2" fontWeight={800} sx={{ color: "text.primary" }} noWrap>{cell.getValue()}</Typography>
      )
    },
    {
      accessorKey: "bookingDate",
      header: "Registry Date",
      Header: ({ column }) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <PendingIcon sx={{ fontSize: 18, color: "warning.main" }} />
          <Typography variant="caption" sx={{ fontWeight: 900, textTransform: "uppercase", letterSpacing: "1px" }}>{column.columnDef.header}</Typography>
        </Stack>
      ),
      Cell: ({ cell }) => (
        <Typography variant="body2" fontWeight={750} color="text.secondary">
          {moment(cell.getValue()).format("MMM DD, HH:mm")}
        </Typography>
      ),
    },
    {
      accessorKey: "totalAmount",
      header: "Revenue",
      Header: ({ column }) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <RevenueIcon sx={{ fontSize: 18, color: "success.main" }} />
          <Typography variant="caption" sx={{ fontWeight: 900, textTransform: "uppercase", letterSpacing: "1px" }}>{column.columnDef.header}</Typography>
        </Stack>
      ),
      Cell: ({ cell }) => (
        <Typography variant="body2" fontWeight={950} sx={{ color: "success.main", bgcolor: alpha(theme.palette.success.main, 0.08), px: 1.5, py: 0.5, borderRadius: "8px", border: "1.5px solid", borderColor: alpha(theme.palette.success.main, 0.1), width: "fit-content" }}>
          ₹{cell.getValue()?.toLocaleString() || 0}
        </Typography>
      ),
    },
    {
      accessorKey: "status",
      header: "State",
      Header: ({ column }) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <SuccessIcon sx={{ fontSize: 18, color: "info.main" }} />
          <Typography variant="caption" sx={{ fontWeight: 900, textTransform: "uppercase", letterSpacing: "1px" }}>{column.columnDef.header}</Typography>
        </Stack>
      ),
      Cell: ({ cell }) => {
        const s = cell.getValue() || "pending";
        const paletteMap = {
          confirmed: theme.palette.success,
          pending: theme.palette.warning,
          cancelled: theme.palette.error
        };
        const colorSet = paletteMap[s] || theme.palette.grey;
        
        return (
          <Chip
            label={s.toUpperCase()}
            size="small"
            sx={{
              fontWeight: 900,
              fontSize: "0.65rem",
              letterSpacing: "0.05em",
              bgcolor: colorSet.main,
              color: "#fff",
              border: "none",
              borderRadius: "8px",
              boxShadow: `0 4px 10px ${alpha(colorSet.main, 0.25)}`,
              "& .MuiChip-label": { px: 1.5, color: "#fff" }
            }}
          />
        );
      },
    },
    {
      id: "actions",
      header: "Dynamics",
      Header: ({ column }) => (
        <Typography variant="caption" sx={{ fontWeight: 900, textTransform: "uppercase", letterSpacing: "1px" }}>{column.columnDef.header}</Typography>
      ),
      Cell: ({ row }) => (
        <Stack direction="row" spacing={1}>
          <Tooltip title="Examine Intelligence"><IconButton size="small" onClick={() => { setSelectedBooking(row.original); setOpenDetails(true); }} sx={{ bgcolor: alpha(theme.palette.secondary.main, 0.1), color: "secondary.main", borderRadius: "10px", "&:hover": { bgcolor: "secondary.main", color: "#fff" } }}><ViewIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
          <Tooltip title="Expunge"><IconButton size="small" onClick={() => handleDelete(row.original._id, row.original.bookingId)} sx={{ bgcolor: alpha(theme.palette.error.main, 0.1), color: "error.main", borderRadius: "10px", "&:hover": { bgcolor: "error.main", color: "#fff" } }}><TrashIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
        </Stack>
      ),
    },
  ], [theme.palette]);

  const table = useMaterialReactTable({
    columns,
    data: bookings,
    muiTablePaperProps: { elevation: 0, sx: { borderRadius: "24px", border: `1px solid ${theme.palette.divider}`, overflow: "hidden", bgcolor: theme.palette.background.paper } },
    muiTableContainerProps: { sx: { maxWidth: '100%', overflowX: 'auto' } },
    muiTableHeadCellProps: { sx: { bgcolor: alpha(theme.palette.background.default, 0.5), py: 2.5, fontWeight: 900, fontSize: "0.7rem", color: theme.palette.text.secondary, letterSpacing: "0.1rem" } }
  });

  const stats = useMemo(() => {
    const total = bookings.length;
    const revenue = bookings.reduce((sum, b) => sum + (b.totalAmount || 0), 0);
    const confirmed = bookings.filter(b => b.status === "confirmed").length;
    const pending = bookings.filter(b => b.status === "pending").length;
    return { total, revenue, confirmed, pending };
  }, [bookings]);

  if (loading && bookings.length === 0) return <ModernLoader text="Interrogating Booking Ledger" />;

  return (
    <>
      <Box sx={{ width: "100%", pb: 4 }}>
        <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}>
          {/* Core Page Header */}
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "center" }}
            mb={{ xs: 3, md: 5 }}
            gap={{ xs: 2.5, md: 3 }}
          >
            <Box>
              <Typography variant="h3" sx={{ fontWeight: 950, letterSpacing: "-0.04em", display: "flex", alignItems: "center", gap: 2.5, fontSize: { xs: "1.75rem", sm: "2.25rem", md: "3rem" } }}>
                <Box sx={{ p: 1, borderRadius: "12px", bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main, display: "flex", flexShrink: 0 }}>
                  <TicketIcon sx={{ fontSize: { xs: 22, md: 28 } }} />
                </Box>
                Booking Dynamics
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 800, mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
                <TrustIcon sx={{ fontSize: 16, color: "success.main" }} /> LIVE TRANSACTION STREAM & SESSION AUTHORIZATIONS
              </Typography>
            </Box>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2.5} sx={{ width: { xs: "100%", sm: "auto" } }}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => fetchBookings(false)}
                fullWidth={isMobile}
                sx={{
                  borderRadius: "14px",
                  px: 3,
                  fontWeight: 900,
                  height: "54px",
                  borderColor: theme.palette.divider,
                  color: theme.palette.text.primary,
                  bgcolor: theme.palette.background.paper
                }}
              >
                Synchronize
              </Button>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                fullWidth={isMobile}
                sx={{
                  py: 1.8,
                  px: 4.5,
                  borderRadius: "16px",
                  fontWeight: 900,
                  background: theme.palette.primary.main,
                  boxShadow: `0 10px 25px -5px ${alpha(theme.palette.primary.main, 0.3)}`,
                  textTransform: "none",
                  "&:hover": { background: theme.palette.primary.dark, transform: "translateY(-2px)" },
                  transition: "all 0.2s"
                }}
              >
                Export Ledger
              </Button>
            </Stack>
          </Stack>

          {/* New Premium Filter Bar */}
          <Paper elevation={0} sx={{ p: 2, mb: 4, borderRadius: "24px", bgcolor: alpha(theme.palette.background.default, 0.5), border: `1px solid ${theme.palette.divider}`, display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
            <Box sx={{ position: "relative", flexGrow: 1, maxWidth: "500px" }}>
              <SearchIcon sx={{ position: "absolute", left: 16, top: "50%", transform: "translateY(-50%)", color: "text.secondary", opacity: 0.5, fontSize: 20 }} />
              <TextField fullWidth placeholder="Search by reference ID, customer, or movie asset..." variant="standard" InputProps={{ disableUnderline: true, sx: { pl: 6, pr: 2, height: 50, fontWeight: 700, fontSize: "14px" } }} />
            </Box>
            <Divider orientation="vertical" flexItem sx={{ my: 1, opacity: 0.5 }} />
            <Stack direction="row" spacing={1}>
              <Chip label="ALL SESSIONS" onClick={() => { }} sx={{ fontWeight: 950, height: 36, bgcolor: "primary.main", color: theme.palette.primary.contrastText, cursor: "pointer" }} />
              <Chip label="CONFIRMED" variant="outlined" onClick={() => { }} sx={{ fontWeight: 900, height: 36, "&:hover": { bgcolor: "success.light" } }} />
              <Chip label="PENDING" variant="outlined" onClick={() => { }} sx={{ fontWeight: 900, height: 36, "&:hover": { bgcolor: "warning.light" } }} />
              <Chip label="CANCELLED" variant="outlined" onClick={() => { }} sx={{ fontWeight: 900, height: 36, "&:hover": { bgcolor: "error.light" } }} />
            </Stack>
            <Box sx={{ ml: "auto" }}>
              <Tooltip title="Advanced Filters">
                <IconButton sx={{ bgcolor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}`, borderRadius: "12px" }}><FilterIcon sx={{ fontSize: 20 }} /></IconButton>
              </Tooltip>
            </Box>
          </Paper>

          <Grid container spacing={4} mb={6}>
            {[
              { label: "Total Bookings", val: stats.total, icon: <TicketIcon sx={{ fontSize: 28 }} />, color: "#666cff", trend: "+5.4%" },
              { label: "Net Revenue", val: `₹${stats.revenue.toLocaleString()}`, icon: <RevenueIcon sx={{ fontSize: 28 }} />, color: "#72e128", trend: "+12.1%" },
              { label: "Confirmed", val: stats.confirmed, icon: <ConfirmedIcon sx={{ fontSize: 28 }} />, color: "#26c6f9", trend: "+2.5%" },
              { label: "Pending Auth", val: stats.pending, icon: <PendingIcon sx={{ fontSize: 28 }} />, color: "#fdb528", trend: "-1.2%" }
            ].map((s, i) => (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={i}>
                <Paper elevation={0} sx={{
                  p: 4, borderRadius: "32px", border: `1px solid ${theme.palette.divider}`, bgcolor: theme.palette.background.paper,
                  transition: "0.3s all", "&:hover": { transform: "translateY(-5px)", boxShadow: `0 20px 40px ${alpha(theme.palette.text.primary, 0.05)}` }
                }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={3}>
                    <Box sx={{ width: 54, height: 54, borderRadius: "16px", bgcolor: alpha(s.color, 0.1), color: s.color, display: "flex", alignItems: "center", justifyContent: "center" }}>{s.icon}</Box>
                    <Chip label={s.trend} size="small" sx={{ fontWeight: 950, fontSize: "10px", bgcolor: s.trend.startsWith("+") ? alpha(theme.palette.success.main, 0.1) : alpha(theme.palette.error.main, 0.1), color: s.trend.startsWith("+") ? theme.palette.success.main : theme.palette.error.main }} />
                  </Stack>
                  <Box>
                    <Typography variant="h3" sx={{ fontWeight: 950, color: theme.palette.text.primary, letterSpacing: "-0.04em", mb: 0.5 }}>{s.val}</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.1em", color: "text.secondary", fontSize: "11px" }}>{s.label}</Typography>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <MaterialReactTable table={table} />
        </motion.div>
      </Box>

      {/* Details Dialog */}
      <Dialog
        open={openDetails}
        onClose={() => setOpenDetails(false)}
        fullWidth
        maxWidth="lg"
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : "28px",
            backgroundImage: "none",
            bgcolor: theme.palette.background.paper,
            boxShadow: theme.palette.mode === 'dark' ? "0 25px 80px -12px rgba(0, 0, 0, 0.7)" : "0 25px 50px -12px rgba(19, 19, 22, 0.25)",
            maxHeight: "92vh",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column"
          }
        }}
      >
        <Box sx={{
          p: { xs: 3, md: 4 },
          borderBottom: "1px solid",
          borderColor: "divider",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          bgcolor: alpha(theme.palette.background.default, 0.5),
          flexShrink: 0
        }}>
          <Stack direction="row" spacing={2.5} alignItems="center">
            <Box sx={{
              p: 1.5,
              borderRadius: "16px",
              bgcolor: "primary.main",
              color: theme.palette.primary.contrastText,
              display: "flex",
              boxShadow: "0 8px 16px -4px " + alpha(theme.palette.primary.main, 0.5)
            }}>
              <TicketIcon sx={{ fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 900, color: theme.palette.text.primary, letterSpacing: "-0.02em" }}>
                Booking Intelligence
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em" }}>
                SESSION TRANSMISSION REPORT
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={() => setOpenDetails(false)} sx={{ bgcolor: alpha(theme.palette.text.primary, 0.03), "&:hover": { bgcolor: alpha(theme.palette.text.primary, 0.06) } }}>
            <CancelIcon />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: { xs: 3, md: 5 }, bgcolor: theme.palette.background.paper, flexGrow: 1, overflowY: "auto" }}>
          {selectedBooking && (
            <Grid container spacing={4}>
              {/* Primary Reference */}
              <Grid size={{ xs: 12 }}>
                <Typography variant="overline" sx={{ fontWeight: 900, color: "primary.main", letterSpacing: "0.15em" }}>TRANSACTION REFERENCE</Typography>
                <Paper elevation={0} sx={{ p: 4, borderRadius: "24px", border: `1.5px solid ${theme.palette.divider}`, bgcolor: alpha(theme.palette.background.default, 0.4), mt: 1.5 }}>
                  <Typography variant="h3" sx={{ fontWeight: 950, color: theme.palette.text.primary, letterSpacing: "-0.04em" }}>
                    {selectedBooking.bookingId?.toUpperCase() || "N/A"}
                  </Typography>
                </Paper>
              </Grid>

              {/* Entity Grid */}
              <Grid size={{ xs: 12, md: 7 }}>
                <Typography variant="caption" sx={{ fontWeight: 800, color: "text.secondary", ml: 1, mb: 1, display: "block" }}>CUSTOMER ENTITY</Typography>
                <Paper elevation={0} sx={{ p: 4, borderRadius: "24px", border: `1.5px solid ${theme.palette.divider}`, bgcolor: alpha(theme.palette.background.default, 0.4), height: "calc(100% - 24px)" }}>
                  <Stack direction="row" spacing={3} alignItems="center">
                    <Avatar sx={{ width: 64, height: 64, bgcolor: alpha(theme.palette.secondary.main, 0.1), color: "secondary.main", fontSize: "1.5rem", fontWeight: 900 }}>
                      {(selectedBooking.customerName || selectedBooking.user?.name || 'G')[0]}
                    </Avatar>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 900, color: theme.palette.text.primary }}>{selectedBooking.customerName || selectedBooking.user?.name || "Guest Holder"}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: "text.secondary" }}>{selectedBooking.customerEmail || selectedBooking.user?.email || "No contact registry"}</Typography>
                      <Chip label={`UID: ${selectedBooking.user?._id?.slice(-8).toUpperCase() || "EXTERNAL"}`} size="small" sx={{ mt: 1.5, fontWeight: 900, fontSize: "10px", bgcolor: alpha(theme.palette.secondary.main, 0.05), border: "1px solid", borderColor: alpha(theme.palette.secondary.main, 0.1) }} />
                    </Box>
                  </Stack>
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, md: 5 }}>
                <Typography variant="caption" sx={{ fontWeight: 800, color: "text.secondary", ml: 1, mb: 1, display: "block" }}>ALLOCATION MATRIX</Typography>
                <Paper elevation={0} sx={{ p: 4, borderRadius: "24px", border: `1.5px solid ${theme.palette.divider}`, bgcolor: alpha(theme.palette.background.default, 0.4), height: "calc(100% - 24px)", textAlign: "center", display: "flex", flexDirection: "column", justifyContent: "center" }}>
                  <Typography variant="caption" sx={{ fontWeight: 900, color: "text.secondary", mb: 1, letterSpacing: "0.1em" }}>ASSIGNED SEATS</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 950, color: "primary.main", letterSpacing: "0.05em" }}>{selectedBooking.seats?.join(", ") || "N/A"}</Typography>
                  <Typography variant="caption" sx={{ mt: 2, fontWeight: 800, color: "success.main" }}>TOTAL REVENUE: ₹{selectedBooking.totalAmount || 0}</Typography>
                </Paper>
              </Grid>

              {/* State Transitions */}
              <Grid size={{ xs: 12 }}>
                <Typography variant="overline" sx={{ fontWeight: 900, color: "primary.main", letterSpacing: "0.15em" }}>STATE MANAGEMENT PROTOCOL</Typography>
                <Paper elevation={0} sx={{ p: 4, borderRadius: "24px", border: `1.5px solid ${theme.palette.divider}`, bgcolor: alpha(theme.palette.background.default, 0.4), mt: 1.5 }}>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <Button
                        fullWidth
                        variant={selectedBooking.status === 'confirmed' ? 'contained' : 'outlined'}
                        color="success"
                        onClick={() => handleStatusUpdate(selectedBooking._id, 'confirmed')}
                        sx={{ borderRadius: "16px", py: 2, fontWeight: 900, textTransform: "none", fontSize: "14px", border: "2px solid" }}
                      >
                        Authorize & Confirm
                      </Button>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <Button
                        fullWidth
                        variant={selectedBooking.status === 'pending' ? 'contained' : 'outlined'}
                        color="warning"
                        onClick={() => handleStatusUpdate(selectedBooking._id, 'pending')}
                        sx={{ borderRadius: "16px", py: 2, fontWeight: 900, textTransform: "none", fontSize: "14px", border: "2px solid" }}
                      >
                        Flag as Pending
                      </Button>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <Button
                        fullWidth
                        variant={selectedBooking.status === 'cancelled' ? 'contained' : 'outlined'}
                        color="error"
                        onClick={() => handleStatusUpdate(selectedBooking._id, 'cancelled')}
                        sx={{ borderRadius: "16px", py: 2, fontWeight: 900, textTransform: "none", fontSize: "14px", border: "2px solid" }}
                      >
                        Terminate Booking
                      </Button>
                    </Grid>
                  </Grid>
                  <Box sx={{ mt: 4, p: 2.5, borderRadius: "16px", bgcolor: alpha(theme.palette.primary.main, 0.03), border: "1px dashed", borderColor: "divider", textAlign: "center" }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                      <TrustIcon sx={{ fontSize: 13 }} /> ALL STATE TRANSITIONS ARE PERMANENTLY LOGGED FOR AUDIT PROTOCOLS
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          )}
        </DialogContent>

        <Box sx={{
          p: { xs: 3, md: 4 },
          borderTop: "1px solid",
          borderColor: "divider",
          display: "flex",
          justifyContent: "flex-end",
          bgcolor: alpha(theme.palette.background.default, 0.5),
          flexShrink: 0
        }}>
          <Button onClick={() => setOpenDetails(false)} sx={{ fontWeight: 800, color: "text.secondary", px: 4, borderRadius: "14px", textTransform: "none" }}>Exit Workspace</Button>
        </Box>
      </Dialog>
    </>
  );
};

export default AdminBookings;